import { RotateCcw, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import type { StorefrontMetafield, StorefrontProduct } from '@/features/storefront/api/storefront';
import {
  formatMetafieldValue,
  hasMetaContent,
  isMetaProse,
  metaTextLines,
} from '@/features/storefront/layouts/product/metafields';
import { RatingLink } from '@/features/storefront/components/StarRating';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { focalStyle } from '@/features/storefront/focal';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { ProductZoom } from '@/features/storefront/layouts/product/ProductZoom';
import { safeHref } from '@/features/storefront/sections/schema';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { colorValues, isHexColor, swatchOf } from '@/lib/color';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';
import { toRichTextHtml } from '@/lib/richText';

/**
 * The pieces every product-page layout is assembled from. A layout family
 * arranges these - it never reimplements them, so pricing, variant rules and
 * sanitisation behave identically in all four looks.
 */

/**
 * One metafield value as a chip: the colour it names, and the text that names
 * it. Shared by the two things that carry a colour - a metaobject entry the
 * merchant pointed at, and a colour metafield typed straight onto the product
 * - so both read the same way down the page.
 */
function ValueChip({ swatch, label }: { swatch: string | undefined; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 border px-2.5 py-1"
      style={{
        borderColor: 'var(--storefront-surface)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {swatch ? (
        <span
          aria-hidden
          className="size-3.5 rounded-full border"
          style={{ backgroundColor: swatch, borderColor: 'var(--storefront-surface)' }}
        />
      ) : null}
      {label}
    </span>
  );
}

/**
 * The product's images: the one being looked at, and a strip to pick another.
 * The strip only appears when there is a choice to make.
 *
 * `thumbs` says where that strip stands. `'below'` is the row under the
 * picture every family has always shown; `'rail'` stands it up as a narrow
 * column to the picture's left, which is what a boutique product page looks
 * like - and which falls back to the row on a phone, where there is no room
 * beside the picture for anything at all.
 *
 * The picture itself opens full screen, where it can be zoomed and panned -
 * see `ProductZoom`. Every family gets that for free, because every family
 * arranges this same gallery.
 */
export function ProductGallery({
  detail,
  aspect = 'square',
  rounded = true,
  thumbs = 'below',
  layout = 'single',
}: {
  detail: StorefrontProduct;
  aspect?: 'square' | 'portrait' | 'wide';
  rounded?: boolean;
  thumbs?: 'below' | 'rail';
  /**
   * `grid` lays every picture out at once, two to a row, the first across
   * both - the editorial way of selling a garment. `single` is one picture
   * and its strip.
   */
  layout?: 'single' | 'grid';
}) {
  const text = useShopContent();
  const [index, setIndex] = useState(0);
  const [zooming, setZooming] = useState(false);
  const shown = Math.min(index, Math.max(detail.images.length - 1, 0));
  const image = detail.images[shown];
  const rail = thumbs === 'rail';

  if (layout === 'grid' && detail.images.length > 1) {
    return (
      <div className="grid min-w-0 grid-cols-2 gap-2 self-start" data-gallery-layout="grid">
        {detail.images.map((each, at) => (
          <button
            key={each.id}
            type="button"
            aria-label={at === 0 ? text('product.zoom') : text('section.show_image', { number: at + 1 })}
            onClick={() => {
              setIndex(at);
              setZooming(true);
            }}
            className={cn(
              'relative min-w-0 cursor-zoom-in overflow-hidden',
              at === 0 && 'col-span-2',
              aspect === 'square' && 'aspect-square',
              aspect === 'portrait' && 'aspect-[4/5]',
              aspect === 'wide' && 'aspect-[16/9]',
            )}
            style={{
              backgroundColor: 'var(--storefront-surface)',
              borderRadius: rounded ? 'var(--storefront-radius)' : undefined,
            }}
          >
            <StorefrontImage
              url={each.url}
              sizes={at === 0 ? '(min-width: 1024px) 50vw, 100vw' : '(min-width: 1024px) 25vw, 50vw'}
              // The first picture spans the row and is the page's main one.
              priority={at === 0}
              alt={each.alt_text ?? detail.name}
              className="size-full max-w-full object-cover"
              style={focalStyle(each.image_focal)}
            />
          </button>
        ))}
        {zooming ? (
          <ProductZoom
            images={detail.images}
            index={shown}
            onIndexChange={setIndex}
            onClose={() => setZooming(false)}
            fallbackAlt={detail.name}
          />
        ) : null}
      </div>
    );
  }

  // Two classes here are load-bearing rather than tidiness, and they answer the
  // two ways a picture escapes the shape `aspect` asked for.
  //
  // `min-w-0`: an `<img>` sized at `100%` reports its *stored* width as its
  // min-content contribution, so without a floor the 1600px rendition becomes
  // the minimum width of whatever column the family puts the gallery in - and
  // the page grows a horizontal scrollbar.
  //
  // `self-start`: a grid or flex item stretches by default, and a stretched
  // item has a definite height, which beats `aspect-ratio`. Left to stretch,
  // the picture grows as tall as the specifications listed beside it instead of
  // staying square. Standing it at the start of the row lets its own width
  // decide its height again.
  const picture = (
    <div
      className={cn(
        'relative w-full min-w-0 self-start overflow-hidden',
        rail && 'flex-1',
        aspect === 'square' && 'aspect-square',
        aspect === 'portrait' && 'aspect-[4/5]',
        aspect === 'wide' && 'aspect-[16/9] max-h-[32rem]',
      )}
      style={{
        backgroundColor: 'var(--storefront-surface)',
        borderRadius: rounded ? 'var(--storefront-radius)' : undefined,
      }}
    >
      {image ? (
        // A button rather than a click handler on the picture: opening the
        // full-screen view is a real action, and it has to be reachable by
        // Tab and by Enter as well as by a pointer.
        <button
          type="button"
          aria-label={text('product.zoom')}
          onClick={() => setZooming(true)}
          className="size-full cursor-zoom-in"
        >
          <StorefrontImage
            url={image.url}
            sizes="(min-width: 1024px) 50vw, 100vw"
            priority
            alt={image.alt_text ?? detail.name}
            className="size-full max-w-full object-cover"
            // The frame is square, 4:5 or 16:9 whatever the photo is, so it
            // is held at the merchant's chosen point; the frame clips a zoom.
            style={focalStyle(image.image_focal)}
          />
        </button>
      ) : null}

      {image ? (
        <span
          aria-hidden
          className="pointer-events-none absolute end-3 bottom-3 grid size-9 place-items-center rounded-full bg-black/45 text-white"
        >
          <ZoomIn className="size-4" />
        </span>
      ) : null}
    </div>
  );

  const strip =
    detail.images.length > 1 ? (
      <div
        className={cn(
          'flex gap-2 overflow-x-auto',
          rail ? 'shrink-0 sm:w-[4.75rem] sm:flex-col sm:overflow-visible' : 'mt-3',
        )}
      >
        {detail.images.map((each, thumbIndex) => (
          <button
            key={each.id}
            type="button"
            aria-label={text('section.show_image', { number: thumbIndex + 1 })}
            aria-current={thumbIndex === index}
            onClick={() => setIndex(thumbIndex)}
            className={cn(
              'size-16 shrink-0 overflow-hidden border transition-opacity',
              rail && 'sm:aspect-[4/5] sm:size-auto sm:w-full',
              thumbIndex === index ? 'opacity-100' : 'opacity-60 hover:opacity-100',
            )}
            style={{
              borderRadius: 'var(--storefront-radius)',
              borderColor:
                thumbIndex === index ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
            }}
          >
            <StorefrontImage
              url={each.url}
              sizes={fixedImageSizes(rail ? 96 : 64)}
              alt=""
              className="size-full object-cover"
              style={focalStyle(each.image_focal)}
            />
          </button>
        ))}
      </div>
    ) : null;

  // Portalled to the body, so which family arranged the gallery - and what
  // that family's columns clip - makes no difference to the full-screen view.
  const zoom = zooming ? (
    <ProductZoom
      images={detail.images}
      index={shown}
      onIndexChange={setIndex}
      onClose={() => setZooming(false)}
      fallbackAlt={detail.name}
    />
  ) : null;

  // Column-reverse on a phone keeps the picture above its own thumbnails; the
  // rail only becomes a rail once there is a row to put it in.
  if (rail) {
    return (
      <div className="flex min-w-0 flex-col-reverse gap-3.5 self-start sm:flex-row">
        {strip}
        {picture}
        {zoom}
      </div>
    );
  }

  return (
    <div className="min-w-0 self-start">
      {picture}
      {strip}
      {zoom}
    </div>
  );
}

export interface BuyBoxProps {
  detail: StorefrontProduct;
  selected: StorefrontProduct['variants'][number] | undefined;
  onSelectVariant: (id: string) => void;
  onAddToCart: (quantity?: number) => void;
  adding: boolean;
  /** How loudly the title reads - each family sets its own size. */
  titleClassName?: string;
  /** Full-width button, for the narrow single-column layouts. */
  wideButton?: boolean;
}

/**
 * Everything between seeing the product and putting it in the cart: brand,
 * name, price, the honest tax note, options and the button.
 */
export function BuyBox({
  detail,
  selected,
  onSelectVariant,
  onAddToCart,
  adding,
  titleClassName = 'text-3xl',
  wideButton = false,
}: BuyBoxProps) {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const { cart: sellsThings, enquiry } = useStorefrontFeatures();
  const hasOptions = detail.variants.some((variant) => Object.keys(variant.options).length > 0);

  return (
    <div>
      {detail.brand ? (
        <p className="text-sm uppercase tracking-wide" style={{ color: 'var(--storefront-muted)' }}>
          {detail.brand}
        </p>
      ) : null}
      <h1 className={cn('mt-1 break-words', titleClassName)} style={STOREFRONT_HEADING_STYLE}>
        {detail.name}
      </h1>

      <div className="mt-4 flex items-baseline gap-3">
        <span className="text-2xl font-semibold">
          {formatMoney(selected?.price ?? detail.price)}
        </span>
        {detail.compare_at_price ? (
          <span className="text-lg line-through" style={{ color: 'var(--storefront-muted)' }}>
            {formatMoney(detail.compare_at_price)}
          </span>
        ) : null}
        <RatingLink average={detail.rating_average} count={detail.rating_count} className="self-center" />
      </div>
      <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
        {text('product.tax_note')}
      </p>

      {detail.short_description ? (
        <p className="mt-5 text-base" style={{ color: 'var(--storefront-muted)' }}>
          {detail.short_description}
        </p>
      ) : null}

      {hasOptions ? (
        <div className="mt-6">
          <p className="mb-2 text-sm font-medium">{text('product.options')}</p>
          <div className="flex flex-wrap gap-2">
            {detail.variants.map((variant) => {
              const label =
                Object.values(variant.options).join(' / ') || text('product.option_standard');
              const isSelected = variant.id === selected?.id;
              return (
                <button
                  key={variant.id}
                  type="button"
                  disabled={!variant.in_stock}
                  onClick={() => onSelectVariant(variant.id)}
                  className="border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  style={{
                    borderRadius: 'var(--storefront-radius)',
                    borderColor: isSelected
                      ? 'var(--storefront-primary)'
                      : 'var(--storefront-surface)',
                    color: isSelected ? 'var(--storefront-primary)' : undefined,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {sellsThings ? (
        <button
          type="button"
          disabled={!selected?.in_stock || adding}
          onClick={() => onAddToCart()}
          className={cn(
            'mt-8 inline-flex h-12 items-center justify-center px-7 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50',
            wideButton ? 'w-full' : 'w-full sm:w-auto',
          )}
          style={{
            backgroundColor: 'var(--storefront-primary)',
            borderRadius: 'var(--storefront-radius)',
          }}
        >
          {!selected?.in_stock
            ? text('product.out_of_stock')
            : adding
              ? text('product.adding')
              : text('product.add_to_cart')}
        </button>
      ) : (
        <EnquiryCall enquiry={enquiry} paths={paths} text={text} wideButton={wideButton} />
      )}

      {sellsThings && detail.after_sale ? <AfterSaleNote afterSale={detail.after_sale} /> : null}
    </div>
  );
}

/**
 * What a shopper may ask for once it arrives - "7-day returns", or
 * "Not returnable" - under the buy button, where the decision is made.
 */
function AfterSaleNote({ afterSale }: { afterSale: NonNullable<StorefrontProduct['after_sale']> }) {
  const text = useShopContent();
  const days = afterSale.window_days;
  const offers = [
    afterSale.returnable ? text('product.after_sale_returns', { days }) : null,
    afterSale.refundable ? text('product.after_sale_refunds', { days }) : null,
    afterSale.replaceable ? text('product.after_sale_replacement', { days }) : null,
  ].filter((line): line is string => Boolean(line));

  return (
    <div className="mt-5 text-sm">
      <p className="flex items-center gap-2 font-medium">
        <RotateCcw className="size-4" aria-hidden />
        {offers.length > 0 ? offers.join(' · ') : text('product.after_sale_none')}
      </p>
      {afterSale.note ? (
        <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
          {afterSale.note}
        </p>
      ) : null}
    </div>
  );
}

/**
 * What stands where the buy button was, on a shop that does not sell.
 *
 * Never disabled and never conditional on stock, unlike the button it
 * replaces. A consultation has no stock, so the ordinary "Out of stock" path
 * would put a dead grey button on every service a clinic offers - which is the
 * one thing this whole setting exists to prevent.
 *
 * The address goes through `paths.menu()` because it is a path the *merchant*
 * typed: that helper is what makes `/pages/contact` absolute against this
 * shop, whether the shop is live or a theme's sample, and what leaves an
 * off-site booking link alone.
 */
function EnquiryCall({
  enquiry,
  paths,
  text,
  wideButton,
}: {
  enquiry: { label: string; target: string };
  paths: ReturnType<typeof useStorefrontPaths>;
  text: ReturnType<typeof useShopContent>;
  wideButton: boolean;
}) {
  // The merchant's own label wins; the theme's copy is what stands in when
  // they have not written one.
  const label = enquiry.label.trim() || text('product.enquire');

  return (
    <Link
      to={paths.menu(enquiry.target)}
      className={cn(
        'mt-8 inline-flex h-12 items-center justify-center px-7 text-sm font-semibold text-white',
        wideButton ? 'w-full' : 'w-full sm:w-auto',
      )}
      style={{
        backgroundColor: 'var(--storefront-primary)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {label}
    </Link>
  );
}

/**
 * One metafield's value, shown the way its type deserves: a set of colours as
 * swatches, a link as a link, a date as a date, and everything else as the
 * plain readable text that a spec row is for.
 */
export function MetaValue({ metafield }: { metafield: StorefrontMetafield }) {
  const text = useShopContent();
  const { entries, field_type: fieldType, value } = metafield;

  // Entries the merchant picked from a list. Only the ones carrying a colour
  // need a chip to be understood; the rest read better as an ordinary list,
  // which is what keeps eight rows of chips from becoming visual noise.
  if (entries.length > 0) {
    if (!entries.some((entry) => swatchOf(entry.values))) {
      return <>{entries.map((entry) => entry.display_name).join(', ')}</>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {entries.map((entry) => (
          <ValueChip
            key={entry.handle}
            swatch={swatchOf(entry.values)}
            label={entry.display_name}
          />
        ))}
      </div>
    );
  }

  // The colour is the value - shown as itself, never as its code. A value that
  // is not a hex has no colour to show, so it falls back to its own text.
  if (fieldType === 'color') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {colorValues(value).map((color) =>
          isHexColor(color) ? (
            <span
              key={color}
              role="img"
              aria-label={color}
              className="size-4 rounded-full border"
              style={{ backgroundColor: color, borderColor: 'var(--storefront-surface)' }}
            />
          ) : (
            <ValueChip key={color} swatch={undefined} label={color} />
          ),
        )}
      </div>
    );
  }

  if (fieldType === 'url') {
    const href = safeHref(String(value ?? ''));
    if (href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-2 break-words"
          style={{ color: 'var(--storefront-primary)' }}
        >
          {href.replace(/^https?:\/\//, '')}
        </a>
      );
    }
  }

  return <>{formatMetafieldValue(value, fieldType, text)}</>;
}

/**
 * The merchant's structured facts about the product.
 *
 * Prose comes first as its own block, then everything else as a two-column
 * table: name on the left, value on the right, one row per fact. The names
 * line up whatever their length, so a shopper reads down a single column
 * instead of hunting for where each fact starts.
 */
export function ProductMeta({
  detail,
  heading,
  className,
}: {
  detail: StorefrontProduct;
  /** The merchant's word over the spec table, where they have written one. */
  heading?: string;
  /**
   * Replaces the block's own top margin.
   *
   * Empty rather than absent when the component tree is spacing this part -
   * see `parts/style.ts`. The rule and the padding under it stay either way:
   * they are what separates the specifications from the buy box, not spacing
   * anybody chose.
   */
  className?: string;
}) {
  const text = useShopContent();
  const shown = detail.metafields.filter(hasMetaContent);
  const prose = shown.filter(isMetaProse);
  const rows = shown.filter((metafield) => !isMetaProse(metafield));

  if (shown.length === 0) return null;

  return (
    <section
      className={cn('border-t pt-6', className ?? 'mt-8')}
      style={{ borderColor: 'var(--storefront-surface)' }}
    >
      {prose.map((metafield) => {
        const lines = metaTextLines(metafield.value);
        return (
          <div key={metafield.key} className="mb-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide">{metafield.name}</h2>
            {lines.length > 1 ? (
              <ul className="mt-3 space-y-1.5 text-sm" style={{ color: 'var(--storefront-muted)' }}>
                {lines.map((line, index) => (
                  <li key={`${index}-${line}`} className="flex gap-2">
                    <span aria-hidden>•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
                {lines[0] ?? ''}
              </p>
            )}
          </div>
        );
      })}

      {rows.length > 0 ? (
        <>
          <h2 className="text-sm font-semibold uppercase tracking-wide">
            {heading ?? text('product.details')}
          </h2>
          <dl className="mt-2 text-sm">
            {rows.map((metafield, index) => (
              <div
                key={metafield.key}
                className={cn(
                  'flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:gap-5',
                  index === 0 ? 'pt-1' : 'border-t',
                )}
                style={index === 0 ? undefined : { borderColor: 'var(--storefront-surface)' }}
              >
                <dt className="sm:w-32 sm:shrink-0" style={{ color: 'var(--storefront-muted)' }}>
                  {metafield.name}
                </dt>
                <dd className="min-w-0 flex-1 break-words">
                  <MetaValue metafield={metafield} />
                </dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
    </section>
  );
}

/** The long-form description, sanitised on the way to the page. */
export function ProductDescription({
  detail,
  heading,
  showHeading = true,
  collapsible = false,
  className,
}: {
  detail: StorefrontProduct;
  /** The merchant's own word over it. */
  heading?: string;
  /**
   * Whether the word is shown at all. On by default, which is the page every
   * theme has always drawn; a merchant whose description opens with its own
   * heading turns it off rather than reading "Description" twice.
   */
  showHeading?: boolean;
  /** Folds a long description behind a click instead of filling the column. */
  collapsible?: boolean;
  /** Replaces the block's own top margin. See `ProductMeta`. */
  className?: string;
}) {
  const text = useShopContent();
  const [open, setOpen] = useState(false);
  if (!detail.description) return null;

  const word = heading ?? text('product.description');
  const label = (
    <h2 className="text-sm font-semibold uppercase tracking-wide">{word}</h2>
  );

  return (
    <div
      className={cn('border-t pt-6', className ?? 'mt-10')}
      style={{ borderColor: 'var(--storefront-surface)' }}
      data-product-description=""
    >
      {showHeading && collapsible ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          className="flex w-full items-center justify-between gap-3 text-start"
        >
          {label}
          <span aria-hidden className="text-lg leading-none">
            {open ? '−' : '+'}
          </span>
        </button>
      ) : showHeading ? (
        label
      ) : null}
      {/*
        Merchant-authored HTML. `toRichTextHtml` sanitizes it here rather than
        trusting what was stored, so a description written straight to the API
        - bypassing the editor - cannot put script on a shopper's page.
        Descriptions predating the editor are plain text and get promoted to
        paragraphs by the same call.
      */}
      {collapsible && showHeading && !open ? null : (
        <div
          className="prose-product mt-3 text-sm leading-relaxed break-words"
          style={{ color: 'var(--storefront-muted)' }}
          dangerouslySetInnerHTML={{ __html: toRichTextHtml(detail.description) }}
        />
      )}
    </div>
  );
}
